import { useCallback, useEffect, useState, type ReactNode } from "react";
import { BlogIndexPage } from "./features/blog/BlogIndexPage";
import { DoorwayTeamsPost } from "./features/blog/DoorwayTeamsPost";
import { doorwayTeamsPostPath } from "./features/blog/DoorwayTeamsPostData";
import {
  HostWithoutGitPost,
  InternalToolHostingPost,
  ShareClaudeSkillsPost,
  ShareVibeCodedAppPost,
  WhereToHostVibeCodedAppPost,
} from "./features/blog/SeoAeoPosts";
import {
  hostWithoutGitPath,
  internalToolHostingPath,
  shareClaudeSkillsPath,
  shareVibeCodedAppPath,
  whereToHostVibeCodedAppPath,
} from "./features/blog/SeoAeoPostsData";
import { ProjectFlow } from "./features/projects/ProjectFlow";
import { AgentSetupPage } from "./features/agents/AgentSetupPage";
import { AgentQuickstartPage, AgentsPage, HumanQuickstartPage } from "./features/agents/AgentsPage";
import { WorkspacePage } from "./features/workspace/WorkspacePage";
import { InvitationAcceptPage } from "./features/workspace/InvitationAcceptPage";
import { TeamAppAccessPage } from "./features/workspace/TeamAppAccessPage";
import { AdminDashboard } from "./features/admin/AdminDashboard";
import { HowItWorksPage } from "./features/guide/HowItWorksPage";
import { signOutCreator, startGoogleSignIn } from "./lib/auth-client";
import { establishManagementSession, resetManagementSessionCache, type ManagementSessionState } from "./lib/manage-session";
import { AppLink, currentAppLocation, subscribeToNavigation, workspacePath } from "./lib/navigation";
import { consumedSensitiveUrlParameter, posthog } from "./lib/posthog";
import { PublicAppChallengePage } from "./features/public-access/PublicAppChallengePage";
import {
  DeviceBrowserApprovalPage,
  captureDeviceApprovalFragment,
  type CapturedDeviceApproval,
} from "./features/workspace/DeviceBrowserApprovalPage";
import { PublicLandingPage } from "./features/public/PublicLandingPage";
import {
  PublicSkillAuthorPage,
  PublicSkillClaimPage,
  PublicSkillDetailPage,
  PublicSkillPublisherPage,
  PublicSkillsLibraryPage,
} from "./features/library/PublicSkillsLibrary";
import { DoorwayBrandIcon } from "./features/public/DoorwayBrandIcon";
import { ManagementGate } from "./features/auth/ManagementGate";
import { takePendingPublishDraft, type ProjectFlowDraft } from "./lib/pending-publish";
import {
  captureTeamSkillsDeviceLink,
  TeamSkillsDeviceConnectPage,
  type CapturedTeamSkillsDeviceLink,
} from "./features/workspace/TeamSkillsDeviceConnectPage";

export function App() {
  const [location, setLocation] = useState(currentAppLocation);
  const [deviceApproval] = useState<CapturedDeviceApproval | null>(() => captureDeviceApprovalFragment());
  const [teamSkillsDeviceLink] = useState<CapturedTeamSkillsDeviceLink | null>(() => captureTeamSkillsDeviceLink());
  useEffect(() => {
    const updateLocation = () => setLocation(currentAppLocation());
    return subscribeToNavigation(updateLocation);
  }, []);

  const rawPath = location.split(/[?#]/, 1)[0]!.replace(/\/$/, "") || "/";
  if (rawPath === "/blog") return <BlogIndexPage />;
  if (rawPath === doorwayTeamsPostPath) return <DoorwayTeamsPost />;
  if (rawPath === hostWithoutGitPath) return <HostWithoutGitPost />;
  if (rawPath === whereToHostVibeCodedAppPath) return <WhereToHostVibeCodedAppPost />;
  if (rawPath === internalToolHostingPath) return <InternalToolHostingPost />;
  if (rawPath === shareClaudeSkillsPath) return <ShareClaudeSkillsPost />;
  if (rawPath === shareVibeCodedAppPath) return <ShareVibeCodedAppPost />;
  if (rawPath === "/agents") return <AgentsPage />;
  if (rawPath === "/docs/quickstart-agents") return <AgentQuickstartPage />;
  if (rawPath === "/docs/quickstart") return <HumanQuickstartPage />;
  if (rawPath === "/how-it-works") return <HowItWorksPage />;
  if (rawPath === "/library") return <PublicSkillsLibraryPage />;
  const libraryParts = rawPath.split("/").filter(Boolean);
  if (libraryParts[0] === "library" && libraryParts.length === 2) {
    return <PublicSkillAuthorPage handle={decodeURIComponent(libraryParts[1]!)} />;
  }
  if (libraryParts[0] === "library" && libraryParts.length === 3) {
    return <PublicSkillDetailPage
      handle={decodeURIComponent(libraryParts[1]!)}
      slug={decodeURIComponent(libraryParts[2]!)}
    />;
  }
  if (rawPath === "/public-access/challenge") return <PublicAppChallengePage />;
  if (rawPath === "/manage/app-access") {
    const query = new URLSearchParams(window.location.search);
    return <TeamAppAccessPage projectId={query.get("project")} returnUrl={query.get("return")} />;
  }
  return <SessionAwareApp rawPath={rawPath} deviceApproval={deviceApproval} teamSkillsDeviceLink={teamSkillsDeviceLink} />;
}

function SessionAwareApp({ rawPath, deviceApproval, teamSkillsDeviceLink }: {
  rawPath: string;
  deviceApproval: CapturedDeviceApproval | null;
  teamSkillsDeviceLink: CapturedTeamSkillsDeviceLink | null;
}) {
  const { session, retrySession } = useManagementSession();
  if (!rawPath.startsWith("/manage")) return <PublicLandingPage session={session} onRetry={retrySession} />;
  const path = rawPath.replace(/^\/manage(?=\/|$)/, "") || "/";
  if (path === "/team/skills/devices/link") {
    const workspaces = session.status === "ready" ? session.workspaces ?? [] : [];
    const user = session.status === "ready" ? session.user : undefined;
    return <ManagementRoute session={session} onRetry={retrySession} purpose="mac-connect">
      <TeamSkillsDeviceConnectPage capture={teamSkillsDeviceLink} workspaces={workspaces} user={user} />
    </ManagementRoute>;
  }
  if (path === "/devices/link" || path === "/devices/rotate-key" || /^\/projects\/[^/]+\/review$/.test(path)) {
    return <ManagementRoute session={session} onRetry={retrySession}><DeviceBrowserApprovalPage approval={deviceApproval} /></ManagementRoute>;
  }
  if (path === "/agent/setup") {
    const operationId = queryParameter("operation");
    const approvalState = queryParameter("state");
    return <AgentSetupRoute operationId={operationId} approvalState={approvalState} session={session} onRetry={retrySession} />;
  }
  if (path === "/admin") return <AdminRoute session={session} onRetry={retrySession} />;
  if (path === "/library") {
    return <ManagementRoute session={session} onRetry={retrySession}><PublicSkillPublisherPage /></ManagementRoute>;
  }
  if (path === "/library/claim") {
    return <ManagementRoute session={session} onRetry={retrySession}>
      <PublicSkillClaimPage token={queryParameter("token") ?? ""} />
    </ManagementRoute>;
  }
  if (path === "/invitations") {
    return <InvitationRoute token={queryParameter("token")} session={session} />;
  }
  if (path === "/workspaces" || path.startsWith("/workspaces/")) {
    const workspaceId = path.split("/")[2] || null;
    return <WorkspaceRoute workspaceId={workspaceId} session={session} onRetry={retrySession} />;
  }
  return <ManageHomePage session={session} onRetry={retrySession} />;
}

function AdminRoute({ session, onRetry }: { session: ManagementSessionState; onRetry: () => void }) {
  return <ManagementRoute session={session} onRetry={onRetry}><AdminDashboard /></ManagementRoute>;
}

function ManagementRoute({ children, session, onRetry, purpose }: {
  children: ReactNode;
  session: ManagementSessionState;
  onRetry: () => void;
  purpose?: "mac-connect" | undefined;
}) {
  if (session.status === "ready") return children;
  return (
    <div className="app-shell management-gate-shell">
      <header className="site-header management-gate-header">
        <a className="wordmark" href="/manage" aria-label="Doorway home">
          <DoorwayBrandIcon className="management-gate-logo" name="logo-tile-green" />
          Doorway
        </a>
        <span className="management-gate-security"><i aria-hidden="true" /> Secure workspace</span>
      </header>
      <main className="workspace-gate management-gate-main"><ManagementGate state={session} onRetry={onRetry} interstitial purpose={purpose} /></main>
    </div>
  );
}

function useManagementSession(): { session: ManagementSessionState; retrySession: () => void } {
  const [session, setSession] = useState<ManagementSessionState>({ status: "checking" });
  const [attempt, setAttempt] = useState(0);
  const retrySession = useCallback(() => {
    resetManagementSessionCache();
    setSession({ status: "checking" });
    setAttempt((current) => current + 1);
  }, []);
  useEffect(() => {
    let active = true;
    void establishManagementSession().then((result) => {
      if (!active) return;
      if (result.status === "ready" && result.user) {
        posthog.identify(result.user.id);
      }
      setSession(result);
    });
    return () => { active = false; };
  }, [attempt]);
  return { session, retrySession };
}

function primaryWorkspace(session: ManagementSessionState) {
  return session.status === "ready" ? session.workspaces?.[0] : undefined;
}

function workspaceHref(workspace: ReturnType<typeof primaryWorkspace>): string {
  return workspacePath(workspace?.id);
}

function queryParameter(name: string): string | null {
  return consumedSensitiveUrlParameter(name) ?? new URLSearchParams(window.location.search).get(name);
}

function WorkspaceRoute({ workspaceId, session, onRetry }: { workspaceId: string | null; session: ManagementSessionState; onRetry: () => void }) {
  const initialWorkspaces = session.status === "ready" ? session.workspaces : undefined;
  return <ManagementRoute session={session} onRetry={onRetry}><WorkspacePage key={workspaceId ?? "default"} workspaceId={workspaceId} {...(initialWorkspaces ? { initialWorkspaces } : {})} /></ManagementRoute>;
}

function InvitationRoute({ token, session }: { token: string | null; session: ManagementSessionState }) {
  return (
    <div className="app-shell invitation-shell">
      <header className="site-header">
        <a className="wordmark" href="/" aria-label="Doorway home">
          <span className="wordmark-icon" aria-hidden="true"><span /></span>
          Doorway
        </a>
        <span className="demo-pill">Workspace invitation</span>
      </header>
      <InvitationAcceptPage token={token} session={session} />
    </div>
  );
}

function AgentSetupRoute({ operationId, approvalState, session, onRetry }: { operationId: string | null; approvalState: string | null; session: ManagementSessionState; onRetry: () => void }) {
  const returnPath = operationId ? agentSetupReturnPath(operationId, approvalState) : null;
  return (
    <div className="app-shell agent-setup-shell agent-oasis-shell">
      <header className="site-header agent-oasis-header">
        <a className="wordmark" href="/manage" aria-label="Doorway home">
          <DoorwayBrandIcon className="agent-oasis-logo" name="logo-tile-green" />
          Doorway
        </a>
        <span className="demo-pill">Secure publishing</span>
      </header>
      <main className="agent-oasis-main">
        {!operationId
          ? <section className="flow-card agent-setup-card"><h1>This setup link is incomplete.</h1><p>Return to Claude or Codex and ask it to start publishing again.</p></section>
          : session.status === "ready"
            ? <AgentSetupPage operationId={operationId} {...(approvalState ? { approvalState } : {})} />
            : <ManagementGate
                state={session}
                onRetry={onRetry}
                {...(returnPath ? { onSignIn: () => startGoogleSignIn({ returnPath }) } : {})}
              />}
      </main>
    </div>
  );
}

function agentSetupReturnPath(operationId: string, approvalState: string | null): string {
  const query = new URLSearchParams({ operation: operationId });
  if (approvalState) query.set("state", approvalState);
  return `/manage/agent/setup?${query.toString()}`;
}

function ManageHomePage({ session, onRetry }: { session: ManagementSessionState; onRetry: () => void }) {
  const workspace = primaryWorkspace(session);
  const pendingDraft = usePendingProjectDraft(session);
  const [signOutState, setSignOutState] = useState<{ pending: boolean; error: string | null }>({
    pending: false,
    error: null,
  });

  return (
    <div className={`app-shell manage-oasis-shell${session.status === "ready" ? " manage-oasis-ready" : ""}`}>
      <header className="site-header manage-oasis-header">
        <a className="wordmark" href="/" aria-label="Doorway home">
          <DoorwayBrandIcon className="manage-oasis-logo" name="logo-tile-green" />
          Doorway
        </a>
        <div className="header-actions">
          <a className="header-link" href="/blog">Blog</a>
          <a className="header-link" href="/library">Skills</a>
          <a className="header-link" href="#plans">Plans</a>
          {session.status === "ready" && <AppLink className="header-link" href={workspaceHref(workspace)}>Workspace</AppLink>}
          {session.status === "ready" && session.isAdmin && (
            <AppLink className="header-link" href="/manage/admin">Admin</AppLink>
          )}
          {session.status === "ready" && session.mode === "google" && session.user && (
            <div className="header-sign-out">
              <button
                className="header-link header-button"
                type="button"
                disabled={signOutState.pending}
                onClick={() => {
                  if (signOutState.pending) return;
                  setSignOutState({ pending: true, error: null });
                  void signOutCreator((url) => {
                    posthog.capture("signed_out");
                    posthog.reset();
                    resetManagementSessionCache();
                    window.location.assign(url);
                  }).catch((error: unknown) => {
                    setSignOutState({
                      pending: false,
                      error: error instanceof Error ? error.message : "Sign-out could not finish.",
                    });
                  });
                }}
              >
                {signOutState.pending ? "Signing out..." : `Sign out ${session.user.name.split(" ")[0]}`}
              </button>
              {signOutState.error && <span className="header-error" role="alert">{signOutState.error}</span>}
            </div>
          )}
          {session.status === "ready" && (
            <AppLink className="demo-pill workspace-pill" href={workspaceHref(workspace)}>{workspace?.name ?? "Your workspace"}</AppLink>
          )}
        </div>
      </header>

      <main className="manage-oasis-main">
        <section className="upload-hero manage-oasis-hero">
          <div className="upload-hero-copy">
            <p className="eyebrow"><span className="manage-oasis-live-dot" aria-hidden="true" />From local folder to shareable link</p>
            <h1>Give your app a front door.</h1>
            <p className="hero-intro">Drag in the app on your computer. Doorway checks the files, keeps private things out, and gives you one link to share.</p>
            <p className="demo-status"><span aria-hidden="true" />One app free. No Git or credit card needed.</p>
            <div className="manage-oasis-trust" aria-label="Doorway publishing protections">
              <span><b aria-hidden="true">✓</b> Read-only check</span>
              <span><b aria-hidden="true">✓</b> Private files stay home</span>
              <span><b aria-hidden="true">✓</b> You choose when to publish</span>
            </div>
          </div>

          <div className="manage-oasis-publisher">
            <div className="manage-oasis-window-bar" aria-hidden="true">
              <span className="manage-oasis-traffic"><i /><i /><i /></span>
              <span className="manage-oasis-address">doorway.app/publish</span>
              <span className="manage-oasis-private"><i /> Private preview</span>
            </div>
            <div id="try-doorway" className="hero-uploader manage-oasis-uploader">
              {session.status === "ready" ? (
                pendingDraft.status === "loading" ? (
                  <section className="flow-card" aria-live="polite">
                    <div className="step-label">Restoring folder</div>
                    <h2>Opening your safe review</h2>
                    <span className="progress-orbit" aria-hidden="true" />
                  </section>
                ) : (
                  <>
                    {pendingDraft.status === "failed" && <p className="error-message" role="alert">{pendingDraft.message}</p>}
                    <ProjectFlow
                      {...(session.workspaces ? { initialWorkspaces: session.workspaces } : {})}
                      {...(pendingDraft.status === "ready" ? { initialDraft: pendingDraft.draft } : {})}
                    />
                  </>
                )
              ) : <ManagementGate state={session} onRetry={onRetry} />}
            </div>
            <div className="manage-oasis-window-foot" aria-hidden="true"><span>Files stay on your Mac</span><span>Publish when you are ready</span></div>
          </div>
        </section>

      </main>
      <SiteFooter />
    </div>
  );
}

type PendingDraftState =
  | { status: "idle" | "loading" }
  | { status: "ready"; draft: ProjectFlowDraft }
  | { status: "failed"; message: string };

function usePendingProjectDraft(session: ManagementSessionState): PendingDraftState {
  const [draftId] = useState(() => new URLSearchParams(window.location.search).get("publish"));
  const [state, setState] = useState<PendingDraftState>(() => draftId ? { status: "loading" } : { status: "idle" });

  useEffect(() => {
    if (!draftId || session.status !== "ready") return;
    let active = true;
    void takePendingPublishDraft(draftId).then((draft) => {
      if (!active) return;
      clearPendingPublishParameter();
      setState(draft
        ? { status: "ready", draft }
        : { status: "failed", message: "This saved folder expired or could not be restored. Choose it again to continue." });
    }).catch((caught: unknown) => {
      if (!active) return;
      clearPendingPublishParameter();
      setState({
        status: "failed",
        message: caught instanceof Error ? caught.message : "Doorway could not restore this folder. Choose it again to continue.",
      });
    });
    return () => { active = false; };
  }, [draftId, session.status]);

  return state;
}

function clearPendingPublishParameter(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("publish");
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function SiteFooter() {
  return (
    <footer className="manage-oasis-footer">
      <a className="footer-wordmark" href="/">Doorway</a>
      <nav className="footer-links" aria-label="Footer navigation"><a href="/how-it-works">How it works</a><a href="/blog">Blog</a><a href="/#plans">Plans</a></nav>
      <p>Turn a local app folder into a link people can actually use.</p>
    </footer>
  );
}
